import { getRefreshToken } from 'core/utils/localStorage/localStorage';
import { isString, throttle } from 'lodash';
import * as yup from 'yup';
import axios, { AxiosResponse } from 'axios';

const generateRequestBody = (refreshToken: string) => ({
    operationName: 'RefreshToken',
    variables: {
        refreshToken,
    },
    query: 'mutation RefreshToken($refreshToken: String!) {refreshToken(refreshToken: $refreshToken) { token, refresh_token}}',
});

const fetchRefreshToken = async (
    gqlUri: string,
    handleToken?: (token: string) => void,
    handleError?: () => void,
): Promise<void> => {
    const refreshMutation = generateRequestBody(getRefreshToken());

    if (!refreshMutation.variables.refreshToken) {
        throw new Error('No refresh token found');
    }

    const options = {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(refreshMutation),
    };

    try {
        const response = await fetch(gqlUri, options);
        const json = await response.json();

        if ('errors' in json) {
            if (handleError) {
                handleError();
            } else {
                console.log('%c [AUTH]: Invalid refresh token', 'color: red');
                throw new Error('Invalid refresh Token');
            }
        }

        const token = json.data.refreshToken.token;

        if (handleToken) {
            handleToken && handleToken(token);
        }
    } catch (error) {
        console.error('[Fetch RefreshToken error]', error);
        if (handleError) {
            handleError();
        }
    }
};

const responseDataSchema = yup.object().shape({
    data: yup.object().shape({
        refreshToken: yup.object().shape({
            token: yup.string().required(),
            refresh_token: yup.string().required(),
        }),
    }),
});

type RefreshTokenResponseData = yup.InferType<typeof responseDataSchema>;
type RefreshTokenResponse = {
    token: string;
    refresh_token: string;
};

const makeGraphQLRequest = async (
    graphqlUri: string,
    body: unknown,
): Promise<AxiosResponse<RefreshTokenResponseData>> => {
    return await axios.post(graphqlUri, body, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

const throttledRequest = throttle(makeGraphQLRequest, 1000); // Executes at most once every 1000ms

export const fetchRefreshTokenGraphQL = async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const graphqlUri = process.env.REACT_APP_GQL_SERVER_URL;

    if (!isString(graphqlUri) || !graphqlUri) {
        console.log('Invalid request url for graphql', { graphqlUri });
        throw new Error('Invalid request url for graphql');
    }

    const response = await throttledRequest(graphqlUri, generateRequestBody(refreshToken));

    if (response.status !== 200) {
        console.log('Error in response', { response });
        throw new Error('Invalid response status');
    }

    const data = await responseDataSchema.validate(response.data);

    return data.data.refreshToken;
};

export default fetchRefreshToken;
